import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, ArrowLeft, Lock, Paperclip, Sparkles } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STAFF_STATUSES = ["aberto", "em_atendimento", "aguardando_usuario", "aguardando_equipe", "resolvido", "fechado"];

const CANNED = [
  "Olá! Sou do suporte VULPIX 🦊 Como posso te ajudar?",
  "Pode enviar um print do erro e uma foto do MAC no app, por favor?",
  "Estamos verificando com o provedor e já retornamos. Obrigado pela paciência! 🙏",
  "Ativação concluída com sucesso! ✅ Pode testar e nos avisar?",
  "Não identifiquei o pagamento ainda. Pode enviar o comprovante do PIX?",
];

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "support";
  const [ticket, setTicket] = useState(null);
  const [msg, setMsg] = useState("");
  const [internal, setInternal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const load = () => api.get(`/support/tickets/${id}`).then(({ data }) => setTicket(data)).catch(() => toast.error("Ticket não encontrado"));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [ticket?.messages?.length]);
  useEffect(() => {
    if (ticket?.status === "fechado") return;
    const t = setInterval(() => { if (!document.hidden) load(); }, 5000);
    return () => clearInterval(t);
  }, [ticket?.status, id]);

  const sendText = async (text, asInternal = false) => {
    const bodyText = (text ?? msg).trim();
    if (!bodyText) return;
    try {
      await api.post(`/support/tickets/${id}/messages`, { message: bodyText, internal: asInternal });
      setMsg("");
      setInternal(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const send = () => sendText(msg, internal);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Envie apenas imagens"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", msg || "");
    try {
      setUploading(true);
      await api.post(`/support/tickets/${id}/upload`, fd);
      setMsg("");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const changeStatus = async (status) => {
    try {
      await api.patch(`/support/staff/tickets/${id}/status`, { status });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!ticket) return <div className="p-6 text-muted-foreground">Carregando...</div>;

  const triage = isStaff ? (ticket.messages || []).find((m) => m.internal && (m.message || "").startsWith("🤖 Pré-triagem")) : null;
  const triageSuggestion = triage
    ? triage.message.replace(/^🤖 Pré-triagem:\s*/, "").replace(/^\[(RESOLVIDO|HUMANO)\]\s*/i, "").trim()
    : "";

  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="ticket-detail">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1" data-testid="ticket-back"><ArrowLeft className="h-4 w-4" /> Voltar</Button>

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl font-bold">{ticket.subject}</h2>
            <p className="text-xs text-muted-foreground">{ticket.category} · {ticket.user_name} ({ticket.user_email})</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={ticket.status} />
            {isStaff && (
              <Select value={ticket.status} onValueChange={changeStatus}>
                <SelectTrigger className="w-44" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAFF_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {triage && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid="ticket-ai-triage">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-primary">
            <Sparkles className="h-4 w-4" /> Triagem do Assistente IA
          </div>
          <p className="whitespace-pre-wrap text-sm text-foreground/90">{triage.message.replace(/^🤖 Pré-triagem:\s*/, "")}</p>
          {triageSuggestion && ticket.status !== "fechado" && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setMsg(triageSuggestion)} data-testid="triage-use-btn">Editar sugestão</Button>
              <Button size="sm" onClick={() => sendText(triageSuggestion)} data-testid="triage-send-btn">Responder com a sugestão</Button>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3 rounded-lg border border-border bg-card p-4">
        {ticket.messages.map((m) => {
          const mine = m.author_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
              <div className={`max-w-[80%] rounded-lg p-3 text-sm ${m.internal ? "border border-amber-500/40 bg-amber-500/10" : mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                <div className="mb-1 flex items-center gap-2 text-[11px] opacity-80">
                  {m.internal && <Lock className="h-3 w-3" />}
                  <span className="font-medium">{m.author_name}</span>
                  <span>· {new Date(m.at).toLocaleString("pt-BR")}</span>
                </div>
                {m.is_photo && m.photo_path && (
                  <a href={`${API}/support/attachments/${m.photo_path}`} target="_blank" rel="noreferrer" className="block">
                    <img src={`${API}/support/attachments/${m.photo_path}`} alt="anexo" loading="lazy"
                         className="mb-1 max-h-64 w-auto rounded-md border border-border/40" data-testid={`msg-photo-${m.id}`} />
                  </a>
                )}
                {m.message && <p className="whitespace-pre-wrap">{m.message}</p>}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {ticket.status !== "fechado" && (
        <div className="space-y-2 rounded-lg border border-border bg-card p-4">
          {isStaff && (
            <div className="flex flex-wrap gap-2" data-testid="quick-replies">
              {CANNED.map((c, i) => (
                <button key={i} type="button" onClick={() => setMsg(c)}
                  className="rounded-full border border-border px-3 py-1 text-xs transition-colors hover:border-primary hover:text-primary"
                  data-testid={`canned-${i}`}>
                  {c.length > 34 ? c.slice(0, 34) + "…" : c}
                </button>
              ))}
            </div>
          )}
          <Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Escreva uma resposta..." data-testid="ticket-reply-input" />
          <div className="flex items-center justify-between">
            {isStaff ? (
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox checked={internal} onCheckedChange={setInternal} data-testid="ticket-internal-checkbox" /> Nota interna
              </label>
            ) : <span />}
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="ticket-file-input" />
              <Button variant="outline" size="icon" onClick={() => fileRef.current?.click()} disabled={uploading} title="Anexar imagem" data-testid="ticket-attach-btn"><Paperclip className="h-4 w-4" /></Button>
              <Button onClick={send} className="gap-1" data-testid="ticket-send-btn"><Send className="h-4 w-4" /> Enviar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
